import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { type ReactElement, useEffect, useRef, useState } from "react";
import { ApiError, apiGet } from "../../lib/api";
import { cn } from "../../lib/utils";
import { type Classement, classementSchema } from "../contract";
import { SignIn } from "../sign-in";
import { Rang } from "./rang";
import { CLASSEMENT, points } from "./viewers-texts";

/**
 * Le classement de la renommee (`/regie/renommee`, ticket 2 du socle) : tous les profils, par renommee decroissante,
 * cinquante par page, les ex aequo a la meme place. `GET /api/viewers/classement?page=&recherche=`.
 *
 * - la recherche part 300 ms apres la derniere frappe et ramene a la page 1 ; le texte est cherche dans le nom affiche
 *   et le pseudo Discord par le serveur ;
 * - un profil qui a un viewer ouvre sa fiche (`/regie/viewers/{id}`, un simple lien) ; un profil Discord seul s'affiche
 *   sans lien ;
 * - le rang se lit sans la couleur (`rang.tsx`) ;
 * - 401 : la connexion ; 403 : une phrase et la connexion ; le reste : une phrase fixe, jamais le texte du serveur.
 *
 * Tout ce que le serveur rend s'affiche comme du texte. Les phrases sont dans `viewers-texts.ts`.
 */

/** Le delai de frappe avant qu'une recherche parte. */
export const DELAI_RECHERCHE_MS = 300;
/** La taille d'une page : celle du serveur (`CLASSEMENT_PAGE`). */
export const PAGE_TAILLE = 50;

const BOUTON =
  "min-h-[44px] rounded-[4px] border-2 border-[var(--lisere)] bg-transparent px-3.5 text-[20px] text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50";

function Avatar({ nom, avatar }: { nom: string; avatar: string | null }): ReactElement {
  const initiale = Array.from(nom.trim())[0]?.toUpperCase() ?? "?";
  if (avatar !== null) {
    return (
      <img
        src={avatar}
        alt=""
        width={36}
        height={36}
        loading="lazy"
        referrerPolicy="no-referrer"
        className="size-9 flex-none rounded-full border-2 border-[var(--lisere)] object-cover"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className="police-nom flex size-9 flex-none items-center justify-center rounded-full bg-muted text-[22px] text-primary"
    >
      {initiale}
    </span>
  );
}

function Ligne({ profil }: { profil: Classement["profils"][number] }): ReactElement {
  const nom = (
    <span className="flex min-w-0 items-center gap-3">
      <Avatar nom={profil.nom} avatar={profil.avatar} />
      <span className="police-nom truncate text-[26px] md:text-[30px]">{profil.nom}</span>
    </span>
  );
  return (
    <li className="grid grid-cols-[3.5rem_minmax(0,1fr)_auto] items-center gap-3 rounded-[4px] bg-card px-3 py-2 md:grid-cols-[4.5rem_minmax(0,1fr)_11rem_7rem] md:gap-4">
      <span className="police-nom text-[26px] text-[#FCDD8E] md:text-[30px]">
        {CLASSEMENT.place(profil.position)}
      </span>
      {profil.viewerId === null ? (
        <span className="flex min-w-0 flex-col">
          {nom}
          <span className="pl-12 text-[18px] text-muted-foreground">{CLASSEMENT.discordSeul}</span>
        </span>
      ) : (
        <a
          href={`/regie/viewers/${encodeURIComponent(profil.viewerId)}`}
          aria-label={CLASSEMENT.ouvrirFiche(profil.nom)}
          className="min-w-0 text-card-foreground no-underline hover:text-[#FCDD8E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {nom}
        </a>
      )}
      <Rang id={profil.rang} className="max-md:col-start-2 max-md:row-start-2 max-md:w-fit" />
      <span className="police-nom text-right text-[26px] text-[#FCDD8E] max-md:col-start-3 max-md:row-start-1 md:text-[30px]">
        {points(profil.renommee)}
      </span>
    </li>
  );
}

export function ClassementPage(): ReactElement {
  const [page, setPage] = useState(1);
  const [saisie, setSaisie] = useState("");
  const [recherche, setRecherche] = useState("");

  // La recherche appliquee : un texte neuf ramene a la premiere page, le meme ne bouge rien.
  const appliquee = useRef("");
  useEffect(() => {
    const minuteur = setTimeout(() => {
      const propre = saisie.trim();
      if (propre !== appliquee.current) {
        appliquee.current = propre;
        setRecherche(propre);
        setPage(1);
      }
    }, DELAI_RECHERCHE_MS);
    return () => clearTimeout(minuteur);
  }, [saisie]);

  const classement = useQuery({
    queryKey: ["viewers", "classement", page, recherche],
    queryFn: () => {
      const parametres = new URLSearchParams({ page: String(page) });
      if (recherche !== "") parametres.set("recherche", recherche);
      return apiGet(`/api/viewers/classement?${parametres.toString()}`, classementSchema);
    },
    placeholderData: keepPreviousData,
    retry: false,
  });

  let corps: ReactElement;
  if (classement.isPending) {
    corps = (
      <p role="status" className="m-0 text-muted-foreground">
        {CLASSEMENT.chargement}
      </p>
    );
  } else if (classement.isError) {
    const statut = classement.error instanceof ApiError ? classement.error.status : null;
    if (statut === 401) {
      corps = <SignIn callbackURL="/regie/renommee" />;
    } else if (statut === 403) {
      corps = (
        <>
          <p className="m-0">{CLASSEMENT.horsAcces}</p>
          <SignIn callbackURL="/regie/renommee" />
        </>
      );
    } else {
      corps = (
        <p role="alert" className="m-0 text-destructive">
          {CLASSEMENT.erreur}
        </p>
      );
    }
  } else {
    const { profils, total } = classement.data;
    const pages = Math.max(1, Math.ceil(total / PAGE_TAILLE));
    corps = (
      <>
        {profils.length === 0 ? (
          <p className="m-0 text-muted-foreground">
            {recherche === "" ? CLASSEMENT.vide : CLASSEMENT.aucunResultat}
          </p>
        ) : (
          <>
            <div
              aria-hidden="true"
              className="hidden grid-cols-[4.5rem_minmax(0,1fr)_11rem_7rem] gap-4 px-3 text-[19px] text-muted-foreground md:grid"
            >
              <span>{CLASSEMENT.colonnes.place}</span>
              <span>{CLASSEMENT.colonnes.nom}</span>
              <span>{CLASSEMENT.colonnes.rang}</span>
              <span className="text-right">{CLASSEMENT.colonnes.renommee}</span>
            </div>
            <ol aria-label={CLASSEMENT.titre} className="m-0 flex list-none flex-col gap-1.5 p-0">
              {profils.map((profil) => (
                <Ligne key={profil.profilId} profil={profil} />
              ))}
            </ol>
          </>
        )}
        <nav
          aria-label={CLASSEMENT.titre}
          className="flex flex-wrap items-center justify-between gap-3"
        >
          <span className="text-muted-foreground">{CLASSEMENT.total(total)}</span>
          <span className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className={BOUTON}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {CLASSEMENT.precedent}
            </button>
            <span>{CLASSEMENT.page(page, pages)}</span>
            <button
              type="button"
              className={BOUTON}
              disabled={page >= pages}
              onClick={() => setPage((p) => p + 1)}
            >
              {CLASSEMENT.suivant}
            </button>
          </span>
        </nav>
      </>
    );
  }

  return (
    <div className={cn("police-texte flex flex-col gap-6 text-[22px] md:text-[24px]")}>
      <a
        href="/regie"
        className="w-fit text-[20px] text-primary no-underline hover:text-[#FCDD8E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {CLASSEMENT.retour}
      </a>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="police-titre m-0 text-[40px] font-normal text-primary">
          {CLASSEMENT.titre}
        </h1>
        <label className="flex items-center gap-2 text-[19px] text-muted-foreground">
          {CLASSEMENT.recherche}
          <input
            type="search"
            value={saisie}
            maxLength={64}
            placeholder={CLASSEMENT.recherchePlaceholder}
            onChange={(event) => setSaisie(event.target.value)}
            className="police-texte w-[220px] rounded-[4px] border-2 border-input bg-card px-2.5 py-1.5 text-[19px] text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </label>
      </div>
      {corps}
    </div>
  );
}
